import { motion, useScroll, useTransform } from 'framer-motion';
import { Search, X, ArrowDown } from 'lucide-react';

const linha = {
  oculta: { y: '110%' },
  visivel: (i) => ({
    y: '0%',
    transition: { duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero({ busca, setBusca, onVerProdutos, total = 92, totalCategorias = 15 }) {
  const { scrollY } = useScroll();
  const yCirculo = useTransform(scrollY, [0, 500], [0, 120]);
  const yGrid = useTransform(scrollY, [0, 500], [0, -60]);

  return (
    <section id="topo" className="relative overflow-hidden bg-mk-blue">
      <motion.div
        style={{ y: yCirculo }}
        className="pointer-events-none absolute -top-40 -right-40 h-[480px] w-[480px] rounded-full bg-mk-bluelight/40 blur-3xl"
      />
      <motion.div
        style={{ y: yGrid }}
        className="pointer-events-none absolute -bottom-48 -left-32 h-[420px] w-[420px] rounded-full bg-mk-red/20 blur-3xl"
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
          backgroundSize: '44px 44px',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-24">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-[11px] sm:text-xs font-bold uppercase tracking-[0.2em] text-white/90"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-mk-red" />
          Vitrine virtual da loja
        </motion.p>

        <h1
          data-testid="hero-title"
          className="mt-5 font-heading font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
        >
          <span className="block overflow-hidden pb-1">
            <motion.span custom={0} variants={linha} initial="oculta" animate="visivel" className="block">
              Encontre tudo
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span custom={1} variants={linha} initial="oculta" animate="visivel" className="block">
              para{' '}
              <span className="relative inline-block">
                sua casa
                <motion.span
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.7, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute -bottom-1 left-0 h-[6px] w-full origin-left rounded-full bg-mk-red"
                />
              </span>
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-5 max-w-xl text-base sm:text-lg text-blue-100/90 leading-relaxed"
        >
          Confira nossos produtos e fale diretamente com a nossa equipe.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl"
        >
          <div className="relative flex-1">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="hero-search-input"
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Busque por produto, marca ou modelo..."
              className="w-full rounded-full bg-white py-3.5 pl-11 pr-11 text-sm sm:text-base text-slate-900 placeholder-slate-400 shadow-xl shadow-mk-bluedark/30 outline-none ring-mk-red focus:ring-2 transition-shadow"
            />
            {busca && (
              <button
                data-testid="hero-search-clear-button"
                onClick={() => setBusca('')}
                aria-label="Limpar busca"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <button
            data-testid="hero-ver-produtos-button"
            onClick={onVerProdutos}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-mk-red px-6 py-3.5 text-sm sm:text-base font-bold text-white shadow-xl shadow-mk-bluedark/30 hover:bg-mk-redbright transition-colors duration-300"
          >
            Ver produtos
            <ArrowDown size={17} />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-wrap gap-x-8 gap-y-3"
        >
          {[
            [String(total), 'produtos no catálogo'],
            [String(totalCategorias), 'categorias'],
            ['2', 'lojas para atender você'],
          ].map(([num, label]) => (
            <div key={label} className="flex items-baseline gap-2">
              <span className="font-heading text-2xl sm:text-3xl font-extrabold text-white">{num}</span>
              <span className="text-xs sm:text-sm text-blue-100/80">{label}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
